import { FontIcon, getTheme, IPivotItemProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import {
  IPDefaultButton,
  IPIconButton,
  IPLink,
  IPModal,
  IPPivot,
  IPPivotItem,
  IPPrimaryButton,
  IPSeparator,
  IPText,
  IPTooltipHost,
} from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import { ChecklistContainer, ChecklistMode } from 'components/Checklist/ChecklistContainer';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import { DialogOk } from 'components/Dialogs/DialogOk';
import DialogYesNo from 'components/Dialogs/DialogYesNo';
import TaskLinkList from 'components/Links/LinkLists/TaskLinkList';
import OverlayLoader from 'components/Loading/OverlayLoader';
import { ErrorMessage } from 'components/Notification/ErrorMessage';
import InfoMessage from 'components/Notification/InfoMessage';
import WarningMessage from 'components/Notification/WarningMessage';
import { LockedUntilStartMessage } from 'components/Tasks/LockedUntilStartMessage';
import {
  cancelIcon,
  globalStackStylesHeight100,
  globalStackTokensGapExtraSmall,
  globalStackTokensGapSmall,
  globalTextStylesBold,
  templateIcon,
} from 'globalStyles';
import Joi from 'joi';
import Task, { TaskWorkflowStatus } from 'models/tasks/task';
import { TaskCheckList } from 'models/tasks/taskHelperClasses';
import TaskType from 'models/tasks/taskType';
import { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiGetWebhooksForType, apiRunWebhooksForTask } from 'services/Api/webhookService';
import { apiRequest } from 'services/Auth/authConfig';
import { capitalizeFirstLetter } from 'utils/string';
import { TaskContext } from '../Context/TaskContext';
import TaskTypeForm from '../Forms/TaskTypeForm';
import { ISingleTaskTypeProps } from '../SingleTask';
import { canUpdateTaskField, TaskFieldTypes } from '../TaskAuthHelper';
import TaskInstanceTab from '../TaskInstanceTab';
import TaskTopTasksBar from '../TaskTopTasksBar';
import TaskWorkflows from '../TaskWorkflows';
import {
  getCompactViewSwitch,
  getTaskViewModalStackStyles,
  getTaskViewModalStyles,
  useCompactViewPivotRemountKey,
} from '../View/TaskRenderHelpers';
import { TaskDetailOwner } from './Details/TaskDetailOwner';
import { MonitoringTaskDetail } from './MonitoringTaskDetail';
import TaskWorkflowDetailsModal from './TaskWorkflowDetailsModal';
import { getTaskPivotTabs } from './taskPivotTabs';
import { useTaskPivotCounts } from './useTaskPivotCounts';
import { TaskAIPane } from './TaskAIPane';

interface IMonitoringTaskProps extends ISingleTaskTypeProps {
  onSaveAsNewTask: () => void;
  onChangeCompletionDate: (completionDate: Date) => void;
}

const MonitoringTask = (props: IMonitoringTaskProps) => {
  const { t } = useTranslation(['task', 'control', 'translation', 'dateTimeComponent']);
  const appContext = useContext(AppContext);
  // With a side pane the two task columns share the room that is left (the same split as NormalTask).
  const hasSidePane = props.sidePane !== undefined;
  const detailsColumnWidth = hasSidePane ? '35%' : '50%';
  const pivotColumnWidth = props.compactView ? (hasSidePane ? '70%' : '100%') : hasSidePane ? '35%' : '50%';
  const [err, setErr] = useState<Joi.ValidationError | undefined>(undefined);
  const [showRemoveDialog, setShowRemoveDialog] = useState<boolean>(false);
  const [showCancelDialog, setShowCancelDialog] = useState<boolean>(false);
  const [showWorkflowDetails, setShowWorkflowDetails] = useState<boolean>(false);
  const [formOffersDocumentUpload, setFormOffersDocumentUpload] = useState<boolean | undefined>(undefined);

  const [newTaskHasWorkflows, setNewTaskHasWorkflows] = useState<boolean>(false);
  const [webhooksLoadedForSeries, setWebhooksLoadedForSeries] = useState<number>(0);
  const [showNoWorkflowsToRunDialog, setShowNoWorkflowsToRunDialog] = useState<boolean>(false);
  const [isUpdating, setIsUpdating] = useState<boolean>(false);
  const pivotRemountKey = useCompactViewPivotRemountKey(props.compactView);
  const { pivotCounts, onActivityCountChange } = useTaskPivotCounts(props.task);
  // Only the activities and instances rules are read from here; a monitoring task always has the classic
  // layout, and disallowSave keeps the Activity tab tied to an existing comment trail as before.
  const pivotTabs = getTaskPivotTabs(props.task, { isNewView: false, disallowSave: true });
  const theme = getTheme();

  //
  // Effects
  //
  useEffect(() => {
    //the flag belongs to the loaded form: hide the upload button again until the new form has loaded
    setFormOffersDocumentUpload(undefined);
  }, [props.task.taskId, props.task.taskTypeId]);

  useEffect(() => {
    const _localizedFields = {
      assignment: t('task:Assignment.Label'),
      startDateTime: t('task:Start.Label'),
      endDateTime: t('task:End.ErrorMessage'),
      name: t('task:Name.Label'),
      deadline: t('task:Deadline.ErrorMessage'),
    };

    const loadWebhooks = async () => {
      try {
        if (!props.task.taskMasterId) return;
        //check if the template that created this event has connected webhooks (which trigger a workflow)
        const accessToken = await appContext.getAccessToken(apiRequest.scopes);
        const webHooks = await apiGetWebhooksForType('task', props.task.taskMasterId, accessToken);
        setNewTaskHasWorkflows(webHooks.length > 0);
      } catch (err) {
        props.onError(err);
      }
    };

    const result = props.task.validate(_localizedFields);
    setErr(result.error);

    if (props.task.taskId === -1 && props.task.taskMasterId && props.task.taskMasterId !== webhooksLoadedForSeries) {
      setWebhooksLoadedForSeries(props.task.taskMasterId);
      loadWebhooks();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.task]);

  //
  // Helpers
  //
  const validate = (fieldName: string): string | undefined => {
    if (err) {
      const fieldError = err.details.find((e) => {
        return e.context?.key === fieldName || e.message.toLowerCase().includes(fieldName.toLowerCase());
      });

      return fieldError ? fieldError.message : undefined;
    }

    return undefined;
  };

  const hasErrors = (): boolean => {
    return err ? true : false;
  };

  const cannotSaveTask = (): boolean => {
    return (props.task.taskId !== -1 && props.task.isEqual(props.orgTask)) || hasErrors();
  };

  const getIsOutlookCreateDisabled = (): boolean => {
    return (
      !props.task.isAssigned() ||
      hasErrors() ||
      props.isActionPending ||
      (props.disallowSchedule ?? false) ||
      !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookCreate, appContext)
    );
  };

  const onCancel = () => {
    if (cannotSaveTask()) {
      props.onCancel();
    } else {
      setShowCancelDialog(true);
    }
  };

  const onUpdateCheckList = (newCheckList: TaskCheckList) => {
    const copyTask = props.task.clone();
    copyTask.checkList = newCheckList;
    props.onUpdateTask(copyTask);
  };

  const getTaskDetails = () => {
    return (
      <MonitoringTaskDetail
        task={props.task}
        orgTask={props.orgTask}
        onUpdate={(newTask: Task) => {
          props.onUpdateTask(newTask);
        }}
        getErrorMessage={validate}
        isActionPending={props.isActionPending}
        tags={props.tags}
        addTagToTaskState={props.addTagToTaskState}
        removeTagFromTaskState={props.removeTagFromTaskState}
        onChangeCompletionDate={props.onChangeCompletionDate}
        canUpdate={props.canUpdate}
        compactView={props.compactView}
        masterTask={props.masterTask}
      />
    );
  };

  const onClickFinish = () => {
    const newTask = props.task.clone();
    newTask.setComplete();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  const onClickArchive = () => {
    const newTask = props.task.clone();
    newTask.setArchived();
    props.onUpdateTask(newTask);
    props.onSave(newTask, false);
  };

  const onClickForm = (taskTypeId: number) => {
    const newTask = props.task.clone();
    newTask.assignForm(taskTypeId > 0 ? appContext.globalDataCache.taskTypes.get(taskTypeId) : undefined);

    props.onUpdateTask(newTask);

    if (taskTypeId > 0) {
      props.setSelectedPivot('TaskType');
    } else if (appContext.isMobileView) {
      props.setSelectedPivot('TaskDetails');
    } else {
      props.setSelectedPivot('Checklist');
    }
  };

  //
  // Render helpers
  //
  const getWorkflowTaskMessage = (newEventHasWorkflows: boolean): JSX.Element | null => {
    if (appContext.isMobileView) return null;
    if (props.task.taskId === -1 && newEventHasWorkflows) {
      //not implemented: monitoring tasks cannot be created manually
      return null;
    } else {
      if (props.task.webhookStatus === TaskWorkflowStatus.Success) {
        return (
          <InfoMessage>
            <IPStack horizontal tokens={globalStackTokensGapExtraSmall}>
              <IPText variant="small"> {t('task:Workflows.Messages.TaskSuccess')} </IPText>
              <IPLink onClick={() => setShowWorkflowDetails(true)}>{t('task:Workflows.Messages.DetailLink')}</IPLink>
            </IPStack>
          </InfoMessage>
        );
      } else if (props.task.webhookStatus === TaskWorkflowStatus.Failure) {
        return (
          <WarningMessage>
            <IPStack horizontal tokens={globalStackTokensGapExtraSmall}>
              <IPText styles={{ root: { color: 'black' } }} variant="small">
                {t('task:Workflows.Messages.TaskFailure')}
              </IPText>
              <IPLink onClick={() => setShowWorkflowDetails(true)}>{t('task:Workflows.Messages.DetailLink')}</IPLink>
            </IPStack>
          </WarningMessage>
        );
      } else {
        return null;
      }
    }
  };

  const getOutlookButton = (): JSX.Element | null => {
    if (!props.task.isSeries()) {
      if (!props.task.eventId) {
        //create
        return (
          <IPStack>
            <IPStack.Item>
              <IPPrimaryButton
                className="isoplanner-task-scheduleinoutlook"
                disabled={getIsOutlookCreateDisabled()}
                onClick={() => {
                  props.createInOutlook();
                }}
              >
                {t('task:Outlook.CreateOutlook')}
              </IPPrimaryButton>
            </IPStack.Item>
          </IPStack>
        );
      } else if (props.task.eventId) {
        //remove
        return (
          <IPStack>
            <IPStack.Item>
              <IPPrimaryButton
                text={t('task:Outlook.RemoveOutlook')}
                disabled={
                  hasErrors() ||
                  props.isActionPending ||
                  !canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.OutlookRemove, appContext)
                }
                onClick={() => {
                  props.removeFromOutlook();
                }}
              />
            </IPStack.Item>
          </IPStack>
        );
      }
    }

    return null;
  };

  const onRunTemplateWorkflows = async (task: Task) => {
    try {
      if (!task.taskMasterId) return;

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const webHooks = await apiGetWebhooksForType('task', task.taskMasterId, accessToken);
      if (webHooks.length === 0) {
        setShowNoWorkflowsToRunDialog(true);
      } else {
        setIsUpdating(true);
        const result = await apiRunWebhooksForTask(task.taskId, accessToken);
        const newTask = props.task.clone();
        newTask.webhookStatus = result;
        props.onUpdateTask(newTask);
        setShowWorkflowDetails(true);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsUpdating(false);
    }
  };

  const onRenderTaskTypePivotItem = (
    itemProps?: IPivotItemProps,
    defaultRender?: (itemProps?: IPivotItemProps) => JSX.Element | null,
  ): JSX.Element | null => {
    //itemScope contains true when in compact view
    if (!itemProps || !itemProps.itemKey || !defaultRender) return null;

    //render default for compact mode
    if (props.compactView === true) {
      return defaultRender(itemProps);
    }

    return (
      <IPStack horizontal tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPText styles={itemProps.itemKey === props.selectedPivot ? globalTextStylesBold : undefined}>
            {itemProps.headerText}
          </IPText>
        </IPStack.Item>
        <IPStack.Item>
          <IPTooltipHost content={t('forms:View.EnterFormMode')}>
            <IPIconButton iconProps={{ iconName: 'Fullscreen' }} onClick={() => props.setFormMode(true)} />
          </IPTooltipHost>
        </IPStack.Item>
      </IPStack>
    );
  };

  //
  // Footer
  //
  const footer = (
    <IPStack horizontal={!appContext.isMobileView} verticalAlign="end" tokens={globalStackTokensGapSmall}>
      <IPStack.Item>
        <IPStack horizontal tokens={globalStackTokensGapSmall}>
          <IPPrimaryButton
            disabled={cannotSaveTask() || props.isActionPending}
            onClick={async () => {
              props.onSave(props.task, false);
            }}
          >
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
          <IPDefaultButton
            onClick={() => {
              onCancel();
            }}
          >
            {t('translation:General.Button.Cancel')}
          </IPDefaultButton>
        </IPStack>
      </IPStack.Item>
      <IPStack.Item grow>
        <IPStack horizontal horizontalAlign={appContext.isMobileView ? undefined : 'space-around'}>
          {getOutlookButton()}
        </IPStack>
      </IPStack.Item>
    </IPStack>
  );

  //
  // Main render
  //
  return (
    <IPModal forceFocusInsideTrap={false} isOpen={props.isOpen} styles={getTaskViewModalStyles(props.windowLevel)}>
      {(props.isLoading || isUpdating) && <OverlayLoader text={t('task:Progress.Save')} />}
      <IPStack
        verticalFill
        tokens={globalStackTokensGapSmall}
        styles={getTaskViewModalStackStyles(appContext.isMobileView, props.compactView, hasSidePane)}
      >
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" horizontalAlign="space-between">
            <IPStack
              horizontal
              verticalAlign="center"
              tokens={globalStackTokensGapSmall}
              styles={{ root: { minWidth: 0 } }}
            >
              {!props.compactView && props.task.isSeries() && (
                <IPStack.Item>
                  <FontIcon
                    {...templateIcon}
                    style={{
                      fontSize: 28,
                      color: appContext.useDarkMode ? theme.palette.themeLight : theme.palette.themeSecondary,
                    }}
                  />
                </IPStack.Item>
              )}
              <IPStack.Item styles={{ root: { minWidth: 0 } }}>
                {!props.compactView && (
                  <IPText variant="xxLarge">
                    {props.task.taskId === -1 && t('task:Title.Monitoring.New')}
                    {props.task.taskId !== -1 && props.task.isSeries() && t('task:Title.Monitoring.Series.Edit')}
                    {props.task.taskId !== -1 && !props.task.isSeries() && t('task:Title.Monitoring.Edit')}
                  </IPText>
                )}
                {props.task.taskId !== -1 && props.compactView && (
                  <IPTooltipHost content={props.task.name}>
                    <IPText block nowrap variant="xxLarge">
                      {capitalizeFirstLetter(props.task.name)}
                    </IPText>
                  </IPTooltipHost>
                )}
              </IPStack.Item>
            </IPStack>
            {!props.compactView && (
              <IPStack.Item>
                <TaskTopTasksBar
                  task={props.task}
                  onClickTemplate={props.copyFromTemplate}
                  onClickFinish={!props.task.isSeries() ? onClickFinish : undefined}
                  onClickArchive={!props.task.isSeries() ? onClickArchive : undefined}
                  onClickFollowUp={props.task.isInstance() ? props.onClickFollowUp : undefined}
                  onClickRemove={() => setShowRemoveDialog(true)}
                  onClickForm={props.task.isSeries() ? onClickForm : undefined}
                  onSaveAsNewTask={props.task.isSeries() ? props.onSaveAsNewTask : undefined}
                  onRunTemplateWorkflows={!props.task.isSeries() ? onRunTemplateWorkflows : undefined}
                  navigateExternal={props.navigateExternal}
                  canUpdate={props.canUpdate}
                  canDelete={props.canDelete}
                  orgTask={props.orgTask}
                  onConvertChecklist={props.task.isSeries() ? props.onConvertChecklist : undefined}
                  canSave={!hasErrors()}
                  isLoading={props.isLoading}
                  isActionPending={props.isActionPending}
                  onUploadEvidence={props.onUploadEvidence}
                  formOffersDocumentUpload={formOffersDocumentUpload}
                  assistantAction={props.assistantAction}
                  onOpenAssistant={props.onOpenAssistant}
                  onInstancesFormUpgraded={props.onSeriesFormUpgraded}
                />
              </IPStack.Item>
            )}
            <IPStack.Item>
              <IPIconButton iconProps={cancelIcon} onClick={onCancel} />
            </IPStack.Item>
          </IPStack>
          <IPStack.Item>
            <TaskDetailOwner
              orgTask={props.orgTask}
              task={props.task}
              onUpdate={props.onUpdateTask}
              canUpdate={props.canUpdate}
            />
          </IPStack.Item>
          <IPSeparator />
          <ErrorMessage error={props.error} customAction={props.onAfterErrorAction} />
          <LockedUntilStartMessage task={props.task} />
          {getWorkflowTaskMessage(newTaskHasWorkflows)}
        </IPStack.Item>
        {props.compactView && (
          <IPStack.Item>
            <TaskTopTasksBar
              task={props.task}
              onClickTemplate={props.copyFromTemplate}
              onClickFinish={!props.task.isSeries() ? onClickFinish : undefined}
              onClickArchive={!props.task.isSeries() ? onClickArchive : undefined}
              onClickFollowUp={props.task.isInstance() ? props.onClickFollowUp : undefined}
              onClickRemove={() => setShowRemoveDialog(true)}
              onClickForm={props.task.isSeries() ? onClickForm : undefined}
              onSaveAsNewTask={props.task.isSeries() ? props.onSaveAsNewTask : undefined}
              onRunTemplateWorkflows={!props.task.isSeries() ? onRunTemplateWorkflows : undefined}
              navigateExternal={props.navigateExternal}
              compact={true}
              canUpdate={props.canUpdate}
              canDelete={props.canDelete}
              orgTask={props.orgTask}
              onConvertChecklist={props.task.isSeries() ? props.onConvertChecklist : undefined}
              canSave={!hasErrors()}
              isLoading={props.isLoading}
              isActionPending={props.isActionPending}
              onUploadEvidence={props.onUploadEvidence}
              formOffersDocumentUpload={formOffersDocumentUpload}
              assistantAction={props.assistantAction}
              onOpenAssistant={props.onOpenAssistant}
              onInstancesFormUpgraded={props.onSeriesFormUpgraded}
            />
          </IPStack.Item>
        )}
        <IPStack.Item grow>
          <IPStack horizontal styles={globalStackStylesHeight100} tokens={globalStackTokensGapSmall}>
            {!props.compactView && (
              <Fragment>
                <IPStack.Item styles={{ root: { width: detailsColumnWidth, minWidth: 0 } }}>
                  {getTaskDetails()}
                </IPStack.Item>
                <IPSeparator vertical></IPSeparator>
              </Fragment>
            )}
            <IPStack.Item styles={{ root: { width: pivotColumnWidth, minWidth: 0 } }}>
              <IPStack verticalFill tokens={globalStackTokensGapSmall}>
                <IPStack.Item>
                  <IPStack horizontal={!appContext.isMobileView} verticalAlign="start">
                    {getCompactViewSwitch(
                      props.task,
                      appContext.isMobileView,
                      props.compactView,
                      t,
                      props.setCompactView,
                      appContext,
                    )}
                    <IPStack.Item grow styles={{ root: { minWidth: 0 } }}>
                      <IPPivot
                        key={pivotRemountKey}
                        selectedKey={props.selectedPivot}
                        onLinkClick={(item) => props.setSelectedPivot(item?.props.itemKey ?? '')}
                        overflowBehavior="menu"
                      >
                        {props.compactView && (
                          <IPPivotItem
                            className="isoplanner-task-pivot-details"
                            key="TaskDetails"
                            itemKey="TaskDetails"
                            headerText={t('task:Pivot.TaskDetails')}
                          />
                        )}
                        {props.hasFormFeature && props.task.taskTypeId && props.task.taskTypeId > 0 && (
                          <IPPivotItem
                            className="isoplanner-task-pivot-tasktype"
                            key="TaskType"
                            itemKey="TaskType"
                            headerText={TaskType.tabNameForTask(
                              props.task.taskTypeId,
                              props.task.formVersion,
                              appContext,
                            )}
                            onRenderItemLink={appContext.isMobileView ? undefined : onRenderTaskTypePivotItem}
                          />
                        )}
                        {(!props.task.taskTypeId || props.task.taskTypeId === 0 || !props.hasFormFeature) && (
                          <IPPivotItem
                            className="isoplanner-task-pivot-checklist"
                            key="Checklist"
                            itemKey="Checklist"
                            headerText={t('task:Pivot.Checklist')}
                            itemCount={pivotCounts.checklist}
                          />
                        )}
                        <IPPivotItem
                          className="isoplanner-task-pivot-context"
                          key="Context"
                          itemKey="Context"
                          headerText={t('task:Pivot.Context')}
                          itemCount={pivotCounts.context}
                        />
                        <IPPivotItem
                          className="isoplanner-task-pivot-library"
                          key="Resources"
                          itemKey="Resources"
                          headerText={t('task:Pivot.Resources')}
                          itemCount={pivotCounts.resources}
                        />
                        {pivotTabs.activities && (
                          <IPPivotItem
                            className="isoplanner-task-pivot-activity"
                            key="Activities"
                            itemKey="Activities"
                            headerText={t('task:Pivot.Activities')}
                            itemCount={pivotCounts.activity}
                          />
                        )}
                        {pivotTabs.instances && (
                          <IPPivotItem
                            className="isoplanner-task-pivot-instances"
                            key="Instances"
                            itemKey="Instances"
                            headerText={t('task:Pivot.Instances')}
                          />
                        )}
                        {props.hasPAFeature && props.task.isSeries() && (
                          <IPPivotItem
                            className="isoplanner-task-pivot-workflows"
                            key="Workflows"
                            itemKey="Workflows"
                            headerText={t('task:Pivot.Workflows')}
                          />
                        )}
                        {props.task.auditTrailId > 0 && (
                          <IPPivotItem
                            className="isoplanner-task-pivot-changelog"
                            key="Audit"
                            itemKey="Audit"
                            headerText={t('task:Pivot.Audit')}
                          />
                        )}
                      </IPPivot>
                    </IPStack.Item>
                  </IPStack>
                </IPStack.Item>
                {props.selectedPivot === 'TaskDetails' && (
                  <Fragment>
                    <IPStack.Item grow style={{ height: appContext.isMobileView ? '40vh' : 0 }}>
                      {getTaskDetails()}
                    </IPStack.Item>
                  </Fragment>
                )}
                <TaskTypeForm
                  isOpen={props.selectedPivot === 'TaskType'}
                  task={props.task}
                  links={props.links}
                  compact={true}
                  compactTaskView={props.compactView}
                  readonly={props.orgTask.isCompleted() || props.task.isSeries()} //the SAVED state decides; cannot fill KPI data on the series
                  allowEdit={props.task.isSeries()}
                  onUpdateData={props.onUpdateKPIData}
                  onSetData={props.onSetKPIData}
                  onUpdateTaskForForm={props.onUpdateTaskForForm}
                  onFormLoaded={(elements) =>
                    setFormOffersDocumentUpload(elements.some((elm) => elm.offersDocumentUpload()))
                  }
                  addLinks={props.addLinkToTaskState}
                  setLinks={props.setLinkToTaskState}
                  removeLink={props.removeLinkFromTaskState}
                  showErrors={props.showFormErrors}
                  onValidate={props.onValidateForm}
                  formMode={props.formMode}
                  onExitFormMode={() => props.setFormMode(false)}
                  onClickFinish={onClickFinish}
                ></TaskTypeForm>
                {props.selectedPivot === 'Checklist' && (
                  <ChecklistContainer
                    task={props.task}
                    orgTask={props.orgTask}
                    onUpdateCheckList={onUpdateCheckList}
                    mode={props.task.isSeries() ? ChecklistMode.Creator : ChecklistMode.User}
                    canUpdate={props.canUpdate}
                  />
                )}
                {props.selectedPivot === 'Resources' && (
                  <TaskLinkList
                    taskLinks={props.links}
                    task={props.task}
                    addLinks={props.addLinkToTaskState}
                    setLinks={props.setLinkToTaskState}
                    removeLink={props.removeLinkFromTaskState}
                    navigateExternal={props.navigateExternal}
                    fixedLinkIds={props.masterTask?.resourceLinkIds}
                  />
                )}
                {props.selectedPivot === 'Activities' && (
                  <IPStack.Item grow>
                    <ActivityTrail
                      auditTrailId={props.task.auditTrailId}
                      commentTrailId={props.task.commentTrailId}
                      taskId={props.task.taskId}
                      authSchemaIds={[props.task.authSchemaId]}
                      onCommentCountChange={onActivityCountChange}
                      onError={props.onError}
                    />
                  </IPStack.Item>
                )}
                {props.selectedPivot === 'Instances' && (
                  <IPStack.Item grow>
                    <TaskInstanceTab
                      taskId={
                        props.task.isInstance()
                          ? props.task.taskMasterId
                            ? props.task.taskMasterId
                            : props.task.taskId
                          : props.task.taskId
                      }
                      activeTaskId={props.task.isInstance() ? props.task.taskId : undefined}
                      windowLevel={props.windowLevel}
                      navigateExternal={props.navigateExternal}
                      reloadToken={props.instancesReloadToken}
                    />
                  </IPStack.Item>
                )}
                {props.selectedPivot === 'Context' && (
                  <IPStack.Item grow>
                    <TaskContext
                      orgTask={props.orgTask}
                      task={props.task}
                      updateTaskForForm={props.onUpdateTaskForForm}
                      updateTask={props.onUpdateTask}
                      onClickFollowUp={props.task.isInstance() ? props.onClickFollowUp : undefined}
                      onLinkFollowUpToTemplate={props.task.isInstance() ? props.onLinkFollowUpToTemplate : undefined}
                      onCreateEmptyFollowUpTask={props.task.isInstance() ? props.onCreateEmptyFollowUpTask : undefined}
                      allowEdit={
                        props.task.isSeries() && //instances cannot modify the context
                        canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.Context, appContext)
                      }
                      allowFollowUpEdit={
                        props.task.isInstance() &&
                        canUpdateTaskField(props.task, props.canUpdate, TaskFieldTypes.Context, appContext)
                      }
                      windowLevel={props.windowLevel}
                      navigateExternal={true}
                    />
                  </IPStack.Item>
                )}
                {props.selectedPivot === 'Workflows' && <TaskWorkflows task={props.task} />}
                {props.selectedPivot === 'Audit' && (
                  <IPStack.Item grow>
                    <AuditTrail auditTrailId={props.task.auditTrailId} />
                  </IPStack.Item>
                )}
              </IPStack>
            </IPStack.Item>
            {/* the AI assistant: a third column beside the task, opened by the AI button */}
            {hasSidePane && <TaskAIPane>{props.sidePane}</TaskAIPane>}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPSeparator styles={{ root: { height: 1 } }} />
        </IPStack.Item>
        <IPStack.Item>{footer}</IPStack.Item>
      </IPStack>
      <DialogConfirmDelete
        onNo={() => {
          setShowRemoveDialog(false);
        }}
        onYes={() => {
          setShowRemoveDialog(false);
          props.onRemove();
        }}
        title={props.task.isSeries() ? t('task:Template.RemoveDialogTitle') : t('task:Remove.DialogTitle')}
        subText={props.task.isSeries() ? t('task:Template.RemoveDialogSubText') : t('task:Remove.DialogSubTitle')}
        hidden={!showRemoveDialog}
        confirmText={!props.task.isSeries() ? t('task:Remove.RemoveDialogConfirmText') : undefined}
      />
      <DialogYesNo
        onNo={() => {
          setShowCancelDialog(false);
        }}
        onYes={() => {
          setShowCancelDialog(false);

          props.onCancel();
        }}
        title={t('task:Cancel.DialogTitle')}
        subText={t('task:Cancel.DialogSubTitle')}
        hidden={!showCancelDialog}
      />
      <DialogOk
        onOk={() => {
          setShowNoWorkflowsToRunDialog(false);
        }}
        title={t('task:Workflows.Dialogs.NoWorkflowsToRun.Title')}
        subText={t('task:Workflows.Dialogs.NoWorkflowsToRun.SubText')}
        hidden={!showNoWorkflowsToRunDialog}
      />
      <TaskWorkflowDetailsModal
        hidden={!showWorkflowDetails}
        task={props.task}
        onClose={() => {
          if (cannotSaveTask()) props.reload();
          setShowWorkflowDetails(false);
        }}
      />
    </IPModal>
  );
};

export default MonitoringTask;
